/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { FC, useEffect, useState } from 'react';
import { ItvSearch } from '../../../components/Itv/ItvSearch';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useGetBingoPageDataLazyQuery,
  type GetBingoPageDataQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-bingo-page-data.query.generated';
import {
  useGetSpinsPageDataLazyQuery,
  type GetSpinsPageDataQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-spins-page-data.query.generated';
import { Spinner } from '../../../components/shared/Spinner';
import { locale } from '../../../consts';
import { Endpoint } from '../../../models/api.model';
import { ItvRowCollection } from '../../../components/Itv/ItvRowCollection';
import { mockImages } from '../../../components/Itv/ItvRowCollection/mocks';
import { BingoFilter } from '../../../components/Itv/ItvRowCollection/components/BingoFilter';
import HeroBannerDraft from './../../../assets/Itv/HeroBannerDraft.jpg';
import HeroBannerDraft1 from './../../../assets/Itv/HeroBannerDraft1.jpg';
import HeroBannerMobDraft from './../../../assets/Itv/HeroBannerMovDraft.jpg';
import HeroBannerMobDraft1 from './../../../assets/Itv/HeroBannerMobDraft1.jpg';
import { ItvCarousel } from '../../../components/Itv/ItvCarousel';
import { ItvCarouselSlide } from '../../../components/Itv/ItvCarousel/ItvCarouselSlide';
import { getImageUrl } from '../../../utils/navigation';
import { RowCollectionLayout } from '../../../components/Itv/ItvRowCollection/layouts/types';
import { buildCollectionTiles } from '../../../components/Itv/ItvRowCollection/utils/collectionTiles';
import { useFetchPlatformPromos } from '../../../hooks/useFetchPlatformPromos';
import { useGetItvCollectionBySlugLazyQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/getItvCollectionBySlug.query.generated';
import { ItvViewAll } from '../../../components/Itv/ItvViewAll';
import './ItvGenericPage.scss';
// Temporary fallback slides while CMS data is wired up
const fallbackSlides: ItvCarouselSlide[] = [
  {
    imgSrc: HeroBannerDraft,
    mobileImgSrc: HeroBannerMobDraft,
    title: 'Welcome to ITV Win Bingo',
    subTitle: 'Lights. Camera. Playtime!',
    buttonLabel: 'Sign up',
    buttonLink: '/promotions/daily-drops',
    termsAndConditions: 'T&Cs apply. 18+',
  },
  {
    imgSrc: HeroBannerDraft1,
    mobileImgSrc: HeroBannerMobDraft1,
    title: 'Welcome to ITV Win Spins',
    subTitle: 'Lights. Camera. Playtime!',
    buttonLabel: 'Sign up',
    buttonLink: '/promotions/daily-drops',
    termsAndConditions: 'T&Cs apply. 18+',
  },
];

type PageDataWithCarousel = GetBingoPageDataQuery['pageData'][number] | GetSpinsPageDataQuery['pageData'][number];

type CarouselSlideNode = NonNullable<NonNullable<PageDataWithCarousel['carouselData']>['slides']>[number];

const createCarouselSlides = (slides?: Array<CarouselSlideNode | null> | null): ItvCarouselSlide[] => {
  if (!slides?.length) return [];

  return slides.reduce<ItvCarouselSlide[]>((acc, slideNode, index) => {
    const slideData = slideNode?.slideData;

    if (!slideData) return acc;

    const [translation] = slideData.translations ?? [];
    const title = translation?.title?.trim();
    const desktopImage = getImageUrl(slideData.desktop_image?.filename_disk);
    const mobileImage = getImageUrl(slideData.mobile_image?.filename_disk);
    const imgSrc = desktopImage || mobileImage;

    if (!title || !imgSrc) {
      return acc;
    }

    acc.push({
      id: `${title}-${index}`,
      title,
      subTitle: translation?.subtitle ?? undefined,
      imgSrc,
      mobileImgSrc: mobileImage || undefined,
      buttonLabel: translation?.cta_text ?? undefined,
      buttonLink: translation?.cta_link ?? undefined,
      openInNewTab: translation?.cta_open_in_new_tab ?? undefined,
      termsAndConditions: translation?.key_terms ?? undefined,
    });

    return acc;
  }, []);
};

export const ItvGenericPage: FC<{ variant: 'bingo' | 'spins' | 'prizes' }> = ({ variant }) => {
  const { subpage } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const platformPromos = useFetchPlatformPromos();

  const [viewAllCollection, setViewAllCollection] = useState<string | null>(null);

  const [fetchBingoPageData, { data: bingoData, loading: bingLoading }] = useGetBingoPageDataLazyQuery();
  const [fetchSpinsPageData, { data: spinsData, loading: spinsLoading }] = useGetSpinsPageDataLazyQuery();
  const [fetchCollectionBySlug, { data: collectionData }] = useGetItvCollectionBySlugLazyQuery();

  useEffect(() => {
    if (!subpage) return;

    const urlParams = new URLSearchParams(location.search);
    const viewAll = urlParams.get('viewAll');

    if (viewAll) {
      setViewAllCollection(viewAll);
      // Fetch the collection data for view all
      const viewAllOpts = {
        variables: { language: locale, slug: viewAll, limit: 2500 },
        context: { endpoint: Endpoint.betzoneDirectusAPI },
      };

      fetchCollectionBySlug(viewAllOpts);
    }

    const opts = {
      variables: { language: locale, pageSlug: subpage, limit: 12 },
      context: { endpoint: Endpoint.betzoneDirectusAPI },
    };

    if (variant === 'bingo') {
      fetchBingoPageData(opts);
    }

    if (variant === 'spins') {
      fetchSpinsPageData(opts);
    }
  }, [subpage, location.search]);
  const data = bingoData || spinsData;

  const [pageData] = data?.pageData || [];
  const loading = bingLoading || spinsLoading || !data || platformPromos.isPromosLoading;

  const getRandomImages = (count = 15): string[] => {
    const available = [...mockImages];
    const result: string[] = [];

    while (result.length < count && available.length > 0) {
      const index = Math.floor(Math.random() * available.length);
      const [url] = available.splice(index, 1);

      if (url) result.push(url);
    }

    return result;
  };

  const heroSlides = createCarouselSlides(pageData?.carouselData?.slides);
  const slidesToRender = heroSlides.length ? heroSlides : fallbackSlides;

  const handleViewAll = (collectionSlug: string, collectionTitle: string): void => {
    const opts = {
      variables: { language: locale, slug: collectionSlug, limit: 2500 },
      context: { endpoint: Endpoint.betzoneDirectusAPI },
    };

    fetchCollectionBySlug(opts);
    setViewAllCollection(collectionSlug);

    const urlParams = new URLSearchParams(location.search);

    urlParams.set('viewAll', collectionSlug);
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
  };

  const handleBackToMain = (): void => {
    setViewAllCollection(null);
    const urlParams = new URLSearchParams(location.search);

    urlParams.delete('viewAll');
    const newSearch = urlParams.toString();

    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  return (
    <div className="itv-generic-page">
      {loading ? (
        <Spinner />
      ) : (
        <>
          <ItvCarousel slides={slidesToRender} autoplayInterval={10000}></ItvCarousel>
          <div className="itv-generic-page__content">
            <div className="itv-generic-page__search">
              <ItvSearch></ItvSearch>
            </div>
            {viewAllCollection && collectionData?.collection?.[0] ? (
              <ItvViewAll
                collection={collectionData.collection[0]}
                onBack={handleBackToMain}
                platformPromos={platformPromos}
              />
            ) : (
              pageData?.collections?.map((collection) => {
                const layoutType = collection?.Collections_id?.layout_type as RowCollectionLayout | undefined;
                const fallbackCount = Math.max(collection?.Collections_id?.games?.length ?? 0, 6);
                const fallbackImages = getRandomImages(fallbackCount);
                const tiles = buildCollectionTiles({
                  collectionId: collection?.id,
                  collectionType: collection?.Collections_id?.type,
                  layoutType,
                  games: collection?.Collections_id?.games,
                  promotions: collection?.Collections_id?.promotions,
                  fallbackImages,
                  stubImageClassName: 'itv-generic-page__demo-image',
                  platformPromos,
                });
                const headerAction =
                  collection?.Collections_id?.type === 'bingo' ? <BingoFilter initial={'ALL'} /> : null;

                return (
                  <ItvRowCollection
                    key={collection?.id}
                    //@ts-ignore
                    layout_type={collection?.Collections_id?.layout_type}
                    //@ts-ignore
                    type={collection?.Collections_id?.type}
                    //@ts-ignore
                    title={`${collection?.Collections_id?.translations[0]?.title}`}
                    headerAction={headerAction}
                    onViewAll={(): void =>
                      handleViewAll(
                        collection?.Collections_id?.translations?.[0]?.slug || '',
                        collection?.Collections_id?.translations?.[0]?.title || ''
                      )
                    }
                  >
                    {tiles}
                  </ItvRowCollection>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};
